/**
 * artiste-products.js
 *
 * Custom Element <artiste-products>
 *
 * Attributes lus sur l'élément :
 *   data-url        → URL de recherche Shopify Search avec filtre metaobjet artiste
 *                     ex: /search?q=*&type=product&filter.p.m.custom.artiste=gid://shopify/Metaobject/123
 *   data-section-id → ID de la section Liquid à re-render (pour le param &sections=)
 *
 * Exclusion du produit courant : Liquid quand `product` est la fiche ; sinon retirer la carte via `data-product-url`
 * sur un fragment **avant** `this.innerHTML = …`, sinon `slideshow-component` compte les points sur le DOM initial (ex. 3 dots pour 2 produits).
 */

class ArtisteProducts extends HTMLElement {
  /**
   * Retire la carte dont le lien produit correspond à `productUrl` (pathname).
   * @param {ParentNode} root
   * @param {string} productUrl
   */
  static removeCurrentProductCards(root, productUrl) {
    try {
      const currentPath = new URL(productUrl, window.location.origin).pathname.replace(/\/$/, '');
      for (const li of root.querySelectorAll('li.c-related-item')) {
        const anchor = li.querySelector('a[href*="/products/"]');
        if (!(anchor instanceof HTMLAnchorElement)) continue;
        const hrefPath = new URL(anchor.getAttribute('href') || anchor.href, window.location.origin).pathname.replace(
          /\/$/,
          ''
        );
        if (hrefPath === currentPath) li.remove();
      }
    } catch {
      /* ignore URL parse errors */
    }
  }

  /**
   * Aligne la visibilité des contrôles slideshow avec le nombre réel de cartes
   * (après exclusion JS du produit courant), même logique que le Liquid :
   * moins de 2 → masqués, 2 à 4 → u-d-mobile uniquement, plus de 4 → desktop sauf layout carousel mobile-only.
   *
   * @param {HTMLElement} host
   * @param {number} itemCount
   */
  static syncSlideshowControls(host, itemCount) {
    const controls = host.querySelector('.c-slideshow-controls');
    if (!controls) return;

    const layoutMobileOnly = controls.getAttribute('data-slideshow-layout-mobile-only') === 'true';

    if (itemCount < 2) {
      controls.remove();
      return;
    }

    if (itemCount >= 2 && itemCount <= 4) {
      controls.classList.add('u-d-mobile');
    } else if (itemCount > 4 && !layoutMobileOnly) {
      controls.classList.remove('u-d-mobile');
    }
  }

  connectedCallback() {
    const searchUrl = this.dataset.url;
    const sectionId = this.dataset.sectionId;

    // Sécurité : si pas d'artiste associé, on ne fait rien
    if (!searchUrl || !sectionId) return;

    this.fetchArtisteProducts(searchUrl, sectionId);
  }

  /**
   * Construit l'URL finale avec le param &sections= pour que Shopify
   * re-render uniquement cette section en JSON.
   */
  buildSectionUrl(searchUrl, sectionId) {
    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('sections', sectionId);
    return url.toString();
  }

  async fetchArtisteProducts(searchUrl, sectionId) {
    try {
      const url = this.buildSectionUrl(searchUrl, sectionId);
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) {
        console.error(`[artiste-products] Fetch échoué : ${response.status}`);
        this.style.display = 'none';
        return;
      }

      const data = await response.json();

      // Shopify retourne { "section-id": "<html>..." }
      const html = data[sectionId];

      if (!html) {
        console.warn('[artiste-products] Aucun HTML retourné pour la section', sectionId);
        this.style.display = 'none';
        return;
      }

      // Parser le HTML retourné
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Récupérer le contenu interne du custom element dans le HTML re-rendu
      const renderedElement = doc.querySelector('artiste-products');

      if (!renderedElement) {
        this.style.display = 'none';
        return;
      }

      let markup = renderedElement.innerHTML;
      const productUrl = this.dataset.productUrl;
      if (productUrl) {
        const preMount = document.createElement('div');
        preMount.innerHTML = markup;
        ArtisteProducts.removeCurrentProductCards(preMount, productUrl);
        markup = preMount.innerHTML;
      }

      this.innerHTML = markup;

      const items = this.querySelectorAll('li.c-related-item');

      if (items.length === 0) {
        this.style.display = 'none';
        return;
      }

      ArtisteProducts.syncSlideshowControls(this, items.length);

      // Dispatcher un event pour notifier les autres composants
      this.dispatchEvent(
        new CustomEvent('artiste-products:loaded', {
          bubbles: true,
          detail: { count: items.length },
        })
      );
    } catch (error) {
      console.error('[artiste-products] Erreur lors du fetch :', error);
      this.style.display = 'none';
    }
  }
}

customElements.define('artiste-products', ArtisteProducts);